import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Filter } from 'lucide-react';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import FloatingActionButtons from 'components/ui/FloatingActionButtons';
import PopupAdvertisement from 'components/ui/PopupAdvertisement';
import HeroSection from './components/HeroSection';
import ExperienceCard from './components/ExperienceCard';

import BookingModal from './components/BookingModal';
import VideoModal from '../../components/ui/VideoModal';
import ReviewsSection from './components/ReviewsSection';
import NearbyAttractionsSection from './components/NearbyAttractionsSection';

const FeaturedExperiencesHub = () => {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const experienceData = [
    {
      id: 1,
      title: 'White Water Rafting Adventure',
      description: 'Experience the thrill of navigating through Grade III rapids in the pristine waters of Kali River. Our expert guides ensure safety while delivering maximum adventure.',
      image: '/assets/images/rafting.jpg',
      category: 'water-sports',
      difficulty: 'Moderate',
      duration: '4-5 hours',
      groupSize: '8-12 people',
      price: 1899,
      rating: 4.8,
      reviews: 124,
      season: ['monsoon', 'winter'],
      highlights: ['Professional guides', 'Safety equipment included', 'Scenic river route'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      safetyInfo: 'Basic swimming knowledge required. All safety equipment provided.',
      includes: ['Life jackets', 'Helmets', 'Professional guide', 'Transportation']
    },
    {
      id: 2,
      title: 'Jungle Safari Experience',
      description: 'Embark on an unforgettable wildlife safari through Dandeli Wildlife Sanctuary. Spot exotic birds, wild elephants, and if lucky, the elusive black panther.',
      image: '/assets/images/safari.jpg',
      category: 'wildlife',
      difficulty: 'Easy',
      duration: '3-4 hours',
      groupSize: '6-8 people',
      price: 1299,
      rating: 4.6,
      reviews: 89,
      season: ['winter', 'summer'],
      highlights: ['Wildlife photography', 'Bird watching', 'Nature guide'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      safetyInfo: 'Suitable for all ages. Maintain silence during safari.',
      includes: ['Safari vehicle', 'Naturalist guide', 'Binoculars', 'Entry permits']
    },
    {
      id: 3,
      title: 'Canopy Zip Lining',
      description: 'Soar through the forest canopy with our exhilarating zip line experience. Multiple zip lines of varying lengths offer different levels of excitement.',
      image: '/assets/images/ziplining.jpg',
      category: 'adventure',
      difficulty: 'Moderate',
      duration: '2-3 hours',
      groupSize: '4-6 people',
      price: 1599,
      rating: 4.9,
      reviews: 156,
      season: ['winter', 'summer'],
      highlights: ['Multiple zip lines', 'Canopy views', 'Professional equipment'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      safetyInfo: 'Weight restrictions apply. Height minimum 4 feet.',
      includes: ['Safety harness', 'Helmet', 'Gloves', 'Professional instructor']
    },
    {
      id: 4,
      title: 'Rock Climbing & Rappelling',
      description: 'Challenge yourself with natural rock formations. Perfect for beginners and experienced climbers alike with routes of varying difficulty.',
      image: '/assets/images/climbing.jpg',
      category: 'adventure',
      difficulty: 'Hard',
      duration: '5-6 hours',
      groupSize: '4-8 people',
      price: 2199,
      rating: 4.7,
      reviews: 78,
      season: ['winter', 'summer'],
      highlights: ['Natural rock faces', 'Multiple difficulty levels', 'Expert instruction'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      safetyInfo: 'Basic fitness required. Medical clearance recommended.',
      includes: ['Climbing gear', 'Safety rope', 'Helmet', 'Certified instructor']
    },
    {
      id: 5,
      title: 'Kayaking Expedition',
      description: 'Paddle through calm backwaters and explore hidden creeks. Perfect for photography enthusiasts and nature lovers.',
      image: '/assets/images/kayaking.jpg',
      category: 'water-sports',
      difficulty: 'Easy',
      duration: '3-4 hours',
      groupSize: '2-4 people',
      price: 1699,
      rating: 4.5,
      reviews: 92,
      season: ['winter', 'summer'],
      highlights: ['Scenic backwaters', 'Bird watching', 'Photography opportunities'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      safetyInfo: 'Basic swimming knowledge preferred. Life jackets provided.',
      includes: ['Kayak', 'Paddle', 'Life jacket', 'Dry bag', 'Guide']
    },
    {
      id: 6,
      title: 'Night Trek & Camping',
      description: 'Experience the wilderness after dark with our guided night trek followed by camping under the stars. Includes bonfire and local cuisine.',
      image: '/assets/images/camping.jpg',
      category: 'trekking',
      difficulty: 'Moderate',
      duration: '12-15 hours',
      groupSize: '8-15 people',
      price: 2899,
      rating: 4.8,
      reviews: 67,
      season: ['winter'],
      highlights: ['Night wildlife spotting', 'Star gazing', 'Bonfire experience'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      safetyInfo: 'Good physical fitness required. Torch and warm clothing essential.',
      includes: ['Camping equipment', 'Meals', 'Guide', 'Torch', 'First aid kit']
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300 && !hasScrolled) {
        setHasScrolled(true);
        setTimeout(() => {
          setShowPopup(true);
        }, 1000);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolled]);

  const filteredExperiences = experienceData?.filter(experience => {
    const seasonMatch = selectedSeason === 'all' || experience?.season?.includes(selectedSeason);
    const categoryMatch = selectedCategory === 'all' || experience?.category === selectedCategory;
    return seasonMatch && categoryMatch;
  });

  const handleBookNow = (experience) => {
    setSelectedExperience(experience);
    setShowBookingModal(true);
  };

  const handleWatchVideo = (experience) => {
    setSelectedVideo(experience);
    setShowVideoModal(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-['Poppins',sans-serif]">
      <Header />
      {/* Popup Advertisement */}
      <PopupAdvertisement 
        isVisible={showPopup}
        onClose={handleClosePopup}
      />
      <main className="pt-16">
        {/* Hero Section */}
        <HeroSection onBookNow={handleBookNow} />

        {/* Filters Section */}
        <section className="py-8 bg-white shadow-sm">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-gray-800">Filter Experiences:</span>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <select 
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="all">All Seasons</option>
                  <option value="winter">Winter</option>
                  <option value="summer">Summer</option>
                  <option value="monsoon">Monsoon</option>
                </select>

                <select 
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  <option value="adventure">Adventure</option>
                  <option value="water-sports">Water Sports</option>
                  <option value="wildlife">Wildlife</option>
                  <option value="trekking">Trekking</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Experiences Grid */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 font-['Playfair Display',serif]">
                Premium Adventure Experiences
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover curated adventures designed to create unforgettable memories in Dandeli's pristine wilderness
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredExperiences?.map((experience, index) => (
                <ExperienceCard
                  key={experience?.id}
                  experience={experience}
                  index={index}
                  onBookNow={() => handleBookNow(experience)}
                  onWatchVideo={() => handleWatchVideo(experience)}
                />
              ))}
            </div>

            {filteredExperiences?.length === 0 && (
              <div className="text-center py-16">
                <h3 className="text-2xl font-semibold text-gray-600 mb-4">No experiences found</h3>
                <p className="text-gray-500">Try adjusting your filters to see more options.</p>
              </div>
            )}
          </div>
        </section>

        {/* Package Deals Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 font-['Playfair Display',serif]">
                Multi-Experience Packages
              </h2>
              <p className="text-xl text-gray-600">
                Save more with our carefully curated experience combinations
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Adventure Combo</h3>
                <p className="text-gray-600 mb-6">White Water Rafting + Rock Climbing + Zip Lining</p>
                <div className="text-3xl font-bold text-green-600 mb-4">₹4,999 <span className="text-lg text-gray-500 line-through">₹5,697</span></div>
                <button 
                  onClick={() => handleBookNow({ title: 'Adventure Combo', price: 4999 })}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
                >
                  Book Package
                </button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Nature Explorer</h3>
                <p className="text-gray-600 mb-6">Jungle Safari + Kayaking + Night Trek</p>
                <div className="text-3xl font-bold text-green-600 mb-4">₹5,499 <span className="text-lg text-gray-500 line-through">₹5,897</span></div>
                <button 
                  onClick={() => handleBookNow({ title: 'Nature Explorer', price: 5499 })}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
                >
                  Book Package
                </button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Complete Adventure</h3>
                <p className="text-gray-600 mb-6">All 6 experiences included with accommodation</p>
                <div className="text-3xl font-bold text-green-600 mb-4">₹9,999 <span className="text-lg text-gray-500 line-through">₹11,495</span></div>
                <button 
                  onClick={() => handleBookNow({ title: 'Complete Adventure', price: 9999 })}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
                >
                  Book Package
                </button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <ReviewsSection />

        {/* Nearby Attractions */}
        <NearbyAttractionsSection />
      </main>
      <Footer />
      <FloatingActionButtons />
      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal
          experience={selectedExperience}
          onClose={() => setShowBookingModal(false)}
          onConfirmBooking={(bookingData) => {
            setShowBookingModal(false);
            navigate('/booking-engine', { state: { selectedExperience: selectedExperience, ...bookingData } });
          }}
        />
      )}
      {/* Video Modal */}
      {showVideoModal && (
        <VideoModal
          isOpen={showVideoModal}
          videoUrl={selectedVideo?.videoUrl}
          title={selectedVideo?.title}
          experience={selectedVideo}
          onClose={() => setShowVideoModal(false)}
        />
      )}
    </div>
  );
};

export default FeaturedExperiencesHub;